'use client';

import React from 'react';
import { useId, type ReactNode } from 'react';

import { Label } from '../../primitives/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../primitives/select';
import { cn } from '../../utils/cn';

export interface UzSelectOption<TValue extends string = string> {
  value: TValue;
  label: string;
  description?: string;
  disabled?: boolean;
}

export interface UzSelectProps<TValue extends string = string> {
  label?: string;
  hint?: string;
  error?: string;
  required?: boolean;
  placeholder?: string;
  value?: TValue;
  defaultValue?: TValue;
  onValueChange?: (value: TValue) => void;
  options: ReadonlyArray<UzSelectOption<TValue>>;
  disabled?: boolean;
  emptyState?: ReactNode;
  containerClassName?: string;
  triggerClassName?: string;
  /**
   * Extra classes for the dropdown menu itself (Radix SelectContent) -
   * e.g. `max-h-60` to cap a long roster list shorter than the
   * primitive's default `max-h-96` (the list scrolls within the cap).
   */
  contentClassName?: string;
  name?: string;
  /**
   * Render each option's `description` on the RIGHT of the label (same row,
   * muted) instead of stacked beneath it. Uses the horizontal space in wide
   * triggers/menus. Default (false) keeps the stacked layout.
   */
  descriptionRight?: boolean;
}

/**
 * Radix `SelectItem` forbids an empty-string `value` (it's reserved for the
 * placeholder / cleared state). Callers across the admin pass `value: ''`
 * to mean "All / Any" filter options, so we transparently swap empty for a
 * sentinel before handing to Radix, and swap it back on change. From the
 * caller's perspective `''` keeps working.
 */
const EMPTY_VALUE_SENTINEL = '__uz_select_empty__';
const toRadix = (v: string | undefined): string | undefined =>
  v === '' ? EMPTY_VALUE_SENTINEL : v;
const fromRadix = (v: string): string => (v === EMPTY_VALUE_SENTINEL ? '' : v);

export const UzSelect = <TValue extends string = string>({
  label,
  hint,
  error,
  required,
  placeholder = 'Select an option',
  value,
  defaultValue,
  onValueChange,
  options,
  disabled,
  emptyState,
  containerClassName,
  triggerClassName,
  contentClassName,
  name,
  descriptionRight = false,
}: UzSelectProps<TValue>): React.JSX.Element => {
  const id = useId();

  return (
    <div className={cn('space-y-1.5', containerClassName)}>
      {label && (
        <Label htmlFor={id} className={cn(disabled && 'opacity-60')}>
          {label}
          {required && (
            <span className="text-destructive ml-1 align-middle text-base leading-none font-semibold">
              *
            </span>
          )}
        </Label>
      )}
      <Select
        value={toRadix(value)}
        defaultValue={toRadix(defaultValue)}
        onValueChange={(v) => {
          // Radix's hidden native <select> (form-bubble input) emits a
          // spurious raw '' change when the controlled value updates before
          // its native <option> set is registered (e.g. a react-hook-form
          // reset right after data loads). A real selection can never be raw
          // '' - Radix forbids empty-string item values; our "empty" options
          // arrive as EMPTY_VALUE_SENTINEL instead. Ignore the glitch so it
          // doesn't wipe the caller's form state.
          if (v === '') return;
          onValueChange?.(fromRadix(v) as TValue);
        }}
        disabled={disabled}
        name={name}
      >
        <SelectTrigger
          id={id}
          aria-invalid={!!error || undefined}
          className={cn(error && 'border-destructive focus:border-destructive', triggerClassName)}
        >
          {/*
           * Radix copies the selected item's content (a stacked div) into the
           * trigger, so a long option label would overflow the box. This
           * wrapper clips the value to a single ellipsised line - `min-w-0`
           * lets the flex child shrink, and `[&_*]:truncate` forces every
           * inner node onto one line.
           */}
          <span className="min-w-0 flex-1 overflow-hidden text-left [&_*]:truncate">
            <SelectValue placeholder={placeholder} />
          </span>
        </SelectTrigger>
        <SelectContent className={contentClassName}>
          {options.length === 0
            ? (emptyState ?? (
                <div className="text-muted-foreground px-2 py-1.5 text-sm">
                  No options available
                </div>
              ))
            : options.map((opt) => (
                <SelectItem
                  key={opt.value}
                  value={opt.value === '' ? EMPTY_VALUE_SENTINEL : opt.value}
                  disabled={opt.disabled}
                >
                  {descriptionRight ? (
                    <div className="flex w-full items-center justify-between gap-3">
                      <span className="truncate">{opt.label}</span>
                      {opt.description && (
                        <span className="text-muted-foreground shrink-0 text-xs">
                          {opt.description}
                        </span>
                      )}
                    </div>
                  ) : (
                    <div className="flex flex-col">
                      <span>{opt.label}</span>
                      {opt.description && (
                        <span className="text-muted-foreground text-xs">{opt.description}</span>
                      )}
                    </div>
                  )}
                </SelectItem>
              ))}
        </SelectContent>
      </Select>
      {error ? (
        <p className="text-destructive text-xs font-medium">{error}</p>
      ) : hint ? (
        <p className="text-muted-foreground text-xs">{hint}</p>
      ) : null}
    </div>
  );
};
